import type { ReactNode } from 'react'
import { useNavigate } from 'react-router'

import { Icon } from '../../components/Icon'
import { PageBand, PageShell } from '../../components/PageShell'
import { EMERGENCY_NUMBER } from '../../lib/emergencyNumber'
import { useFocusOnMount } from './focus'

/**
 * The end of the booking. Deliberately no header and no back arrow: every
 * control here leads to 112 or away, never into the form.
 */
export function StopScreen({
  offerRestart,
  onRestart,
}: {
  offerRestart: boolean
  onRestart: () => void
}): ReactNode {
  const navigate = useNavigate()
  const heading = useFocusOnMount<HTMLHeadingElement>()
  return (
    <PageShell measure={640} as="main">
      <PageBand
        className="flex flex-1 flex-col"
        contentClassName="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-16 text-center"
      >
        <span className="grid h-14 w-14 place-items-center rounded-full bg-sos-bg text-sos">
          <Icon name="sos" size={26} />
        </span>
        <h1
          ref={heading}
          tabIndex={-1}
          className="font-display text-d-xs font-medium tracking-display text-ink-1 focus:outline-none"
        >
          Call {EMERGENCY_NUMBER} now
        </h1>
        <p className="max-w-xs text-[13px] leading-relaxed text-ink-2">
          Or go straight to the nearest emergency department. Do not wait for a
          booking. Medcor Transport cannot be used in an emergency.
        </p>
        {/* The number is text as well as a link: tel: does nothing on most
            desktops, and someone may be dialling from another phone. */}
        <a
          href={`tel:${EMERGENCY_NUMBER}`}
          className="flex w-full max-w-xs items-center justify-center gap-2 rounded-r3 bg-sos px-4 py-3 text-[15px] font-semibold text-sos-bg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sos"
        >
          <Icon name="phone" size={18} /> Call {EMERGENCY_NUMBER}
        </a>
        <button
          type="button"
          onClick={() => void navigate('/')}
          className="rounded-r3 border border-line-2 px-4 py-2 text-[13px] font-medium text-ink-1 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          Back to Medcor home
        </button>
        {offerRestart && (
          <button
            type="button"
            onClick={onRestart}
            className="text-[12px] text-ink-3 underline underline-offset-2 hover:text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Start a new, non-emergency booking
          </button>
        )}
      </PageBand>
    </PageShell>
  )
}
